import { motion } from "framer-motion";
import { Check } from "lucide-react";
import { useSiteImages } from "@/hooks/useSiteImages";

const products = [
  {
    id: "alkaline",
    imgKey: "product_alkaline",
    name: "Nyla Alkaline",
    tag: "pH 8–8.5 · Precisely Maintained",
    keywords: ["pH 8–8.5", "Mineral Rich", "Smooth"],
    sizes: "330 ml · 660 ml",
    desc: "For those who appreciate the finer details. An alkaline pH of 8–8.5, achieved through precise mineral enrichment. The elevated Magnesium and Calcium profile gives Nyla Alkaline its signature smooth, clean taste. At Nyla, alkalinity is a product characteristic we state precisely — one part of a larger story of purity, craft and presentation.",
    minerals: "Elevated Magnesium & Calcium · Alkaline profile",
    benefits: ["Alkaline pH of 8–8.5 as a defining product characteristic", "Elevated Magnesium & Calcium for a smooth, refined taste", "Every batch verified for pH stability and mineral balance"],
    tagline: "Smooth by design. Alkaline by nature.",
  },
  {
    id: "balanced",
    imgKey: "product_balanced",
    name: "Nyla pH Balanced",
    tag: "pH 7.5 · Gently Balanced",
    keywords: ["pH 7.5", "Gentle", "Everyday"],
    sizes: "330 ml · 660 ml",
    desc: "For the everyday elevated. The sweet spot of everyday hydration — neither acidic nor aggressively alkaline. A gentle mineral profile crafted for daily drinking, with a soft, rounded taste that complements every meal and every moment of the day.",
    minerals: "Balanced Magnesium & Calcium · Gentle everyday profile",
    benefits: ["Gentle pH 7.5 crafted for everyday drinking", "Balanced minerals for a soft, rounded taste", "Complements every meal and every moment"],
    tagline: "Welcome to balanced hydration.",
  },
  {
    id: "sparkling",
    imgKey: "product_sparkling",
    name: "Nyla Sparkling",
    tag: "Delicately Carbonated · Crisp Finish",
    keywords: ["Refresh", "Spark", "Revive"],
    sizes: "330 ml · 660 ml",
    desc: "For moments worth marking. Nyla Sparkling Air Water is delicately carbonated to deliver a lively, invigorating hydration experience with every sip. The same atmospheric purity you love — now with a whisper of effervescence that celebrates every occasion, every meal, every pause.",
    minerals: "Delicately mineralised · Fine, persistent bubbles",
    benefits: ["Awakens the palate without heaviness", "Pairs beautifully with fine cuisine", "Zero sugar, zero calories, endlessly refreshing"],
    tagline: "Awaken your senses. Celebrate every sip.",
  },
];

export const Products = () => {
  const imgs = useSiteImages();
  return (
  <section id="products" data-testid="products-section" className="py-24 md:py-32 lg:py-40 bg-black text-[#F5F5F7]">
    <div className="max-w-7xl mx-auto px-6 md:px-12">
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.8 }}
        className="text-center mb-16 md:mb-24 max-w-3xl mx-auto"
      >
        <img src="/nyla-logo-white.png" alt="Nyla" className="h-20 w-auto mx-auto mb-8 opacity-95" />
        <p className="text-xs md:text-sm tracking-[0.2em] uppercase font-semibold text-[#86868B] mb-6">What Nature Provides</p>
        <h1 data-testid="products-heading" className="font-display text-4xl md:text-5xl lg:text-6xl leading-tight tracking-tight font-bold">
          Three creations. One standard of perfection.
        </h1>
        <p className="mt-6 text-lg leading-relaxed text-[#86868B]">
          Every bottle begins as the same pure atmospheric water. The craft — and the mood — is what
          sets each one apart. Choose your ritual.
        </p>
      </motion.div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
        {products.map((p, i) => (
          <motion.div
            key={p.id}
            data-testid={`product-${p.id}`}
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.8, delay: i * 0.15 }}
            className="group bg-[#0A0A0B] border border-white/10 rounded-3xl overflow-hidden hover:-translate-y-1 transition-transform duration-300 ease-out flex flex-col"
          >
            <div className="h-[360px] lg:h-[440px] overflow-hidden">
              <img
                src={imgs[p.imgKey]}
                alt={`${p.name} Air Water bottle`}
                loading="lazy"
                className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-700 ease-out"
              />
            </div>
            <div className="p-7 lg:p-9 flex-1 flex flex-col">
              <h3 className="font-display text-2xl md:text-3xl tracking-tight font-bold mb-3">{p.name}</h3>
              <p className="inline-block self-start px-4 py-1.5 rounded-full border border-white/20 text-[11px] tracking-[0.15em] uppercase text-[#86868B] mb-5">
                {p.tag}
              </p>
              <div className="flex flex-wrap gap-2 mb-5">
                {p.keywords.map((k) => (
                  <span
                    key={k}
                    data-testid={`product-${p.id}-keyword-${k.toLowerCase()}`}
                    className="text-[10px] tracking-[0.2em] uppercase font-semibold text-[#4ADE80] bg-[#4ADE80]/10 border border-[#4ADE80]/25 rounded-full px-3 py-1"
                  >
                    {k}
                  </span>
                ))}
              </div>
              <p className="text-sm md:text-base leading-relaxed text-[#86868B] mb-5">{p.desc}</p>
              <p className="text-xs text-[#F5F5F7]/60 border-t border-white/10 pt-4 mb-5">{p.minerals}</p>
              <ul className="space-y-2.5 mb-5">
                {p.benefits.map((b) => (
                  <li key={b} className="flex items-start gap-3 text-sm text-[#F5F5F7]/80 leading-relaxed">
                    <Check className="w-4 h-4 mt-0.5 shrink-0 text-[#4ADE80]" strokeWidth={2} />
                    {b}
                  </li>
                ))}
              </ul>
              <div className="mt-auto pt-5 border-t border-white/10 flex items-center justify-between gap-3">
                <p className="text-[11px] tracking-[0.15em] uppercase font-semibold text-[#F5F5F7]/60">
                  Available in {p.sizes}
                </p>
                <p className="text-[11px] tracking-[0.15em] uppercase font-semibold text-[#4ADE80]/80">
                  Signature
                </p>
              </div>
              <p className="mt-5 text-sm italic text-[#F5F5F7]/70">"{p.tagline}"</p>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  </section>
  );
};
